import { useState } from "react"
import { Breadcrumb, TitleScreen } from "components/Core"
import { CoverageSearchForm } from "./components/CoverageSearchForm"
import { CoverageResults } from "./components/CoverageResults"
import { TransferForm } from "./components/TransferForm"
import { CourierData, FormData, RouteData, TransferFormData } from "./types"
import useCombinedCoverageBFF from "hooks/bff_entities/useCombinedCoverageBFF"
import Swal from "sweetalert2";
import { useEffect } from "react";


const ListScreen = () => {
  // State
  const [isFormCollapsed, setIsFormCollapsed] = useState(false)
  const [searchResults, setSearchResults] = useState<any>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [resultsPerPage] = useState(10)
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set())
  const [selectedCoverages, setSelectedCoverages] = useState<Set<string>>(new Set())
  const [showTransferForm, setShowTransferForm] = useState(false)
  const { post_advance_search_combined_coverages_action, post_transfer_combined_coverages_action } = useCombinedCoverageBFF()
  const [duplicatedCoverageId, setDuplicatedCoverageId] = useState<string | null>(null);
  // Form state
  const [formData, setFormData] = useState<FormData>({
    query: "",
    status: true,
    route: {
      query: "",
      source: {
        type: "Source",
        data: {
          id: "",
          alias: "",
          name: ""
        }
      },
      target: {
        type: "Location",
        data: {
          criteria0: "",
          criteria1: "",
          criteria2: "",
          criteria3: "",
          criteria4: ""
        }
      }
    },
    courier: {
      query: "",
      name: "",
      alias: "",
      shippingType: "",
      deliveryType: "",
      channel: ""
    }
  })

  const cleanFormData = (formData: FormData) => {
    const cleaned: { [key: string]: any } = { ...formData }
    
    // Remove empty fields
    Object.keys(cleaned).forEach(key => {
      const value = cleaned[key]
      if (value === '' || value === null || value === undefined || 
          (Array.isArray(value) && value.length === 0)) {
        delete cleaned[key]
      }
    })
    
    // Clean route object if it exists
    if (cleaned.route) {
      const route: { [key: string]: any } = { ...cleaned.route }

       // Check if all courier values are empty
       if (Object.values(route).every(val => 
        val === '' || val === null || val === undefined)) {
        delete cleaned.route
      } else {
        // Remove empty fields within courier
        Object.keys(route).forEach(key => {
          const value = route[key]
          if (value === '' || value === null || value === undefined) {
            delete route[key]
          }
        })
        cleaned.route = route
      }
      
      // Check and remove empty source
      if (route.source && Object.values(route.source.data).every(val => 
          val === '' || val === null || val === undefined)) {
        delete route.source
      }
      
      // Check and remove empty target
      if (route.target && Object.values(route.target.data).every(val => 
          val === '' || val === null || val === undefined)) {
        delete route.target
      }
      
      cleaned.route = route as FormData['route']
    }

    // Clean courier object if it exists
    if (cleaned.courier) {
      const courier = { ...cleaned.courier }
      
      // Check if all courier values are empty
      if (Object.values(courier).every(val => 
          val === '' || val === null || val === undefined)) {
        delete cleaned.courier
      } else {
        // Remove empty fields within courier
        Object.keys(courier).forEach(key => {
          const value = courier[key]
          if (value === '' || value === null || value === undefined) {
            delete courier[key]
          }
        })
        cleaned.courier = courier
      }
    }
    
    return cleaned as Partial<FormData>
  }

  useEffect(() => {
    if (searchResults) {
      handleFormSubmit(formData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);
  
  // Handlers
  const handleFormSubmit = async (formData: FormData) => {
    const skipRecords = (currentPage - 1) * resultsPerPage
    const cleanedFormData = cleanFormData(formData)

    const body = {
      skipRecords,
      maxRecords: resultsPerPage,
      filter: cleanedFormData
    }
    
    try {
      const response = await post_advance_search_combined_coverages_action({ body })
      setSearchResults(response)
      setIsFormCollapsed(true)
    } catch (error) {
      // Error is already handled by the hook with SweetAlert
      console.error('Search failed:', error)
    }
  }

  const handleCoverageSelect = (coverageId: string) => {
    setSelectedCoverages(prev => {
      const newSet = new Set(prev)
      if (newSet.has(coverageId)) {
        newSet.delete(coverageId)
      } else {
        newSet.add(coverageId)
      }
      return newSet
    })
  }

  const handleSelectAll = () => {
    if (selectedCoverages.size === searchResults.records.length) {
      setSelectedCoverages(new Set())
    } else {
      const allIds = searchResults.records.map((coverage: any) => coverage.id)
      setSelectedCoverages(new Set(allIds))
    }
  }

  const handleToggleExpansion = (cardId: string) => {
    setExpandedCards(prev => {
      const newSet = new Set(prev)
      if (newSet.has(cardId)) {
        newSet.delete(cardId)
      } else {
        newSet.add(cardId)
      }
      return newSet
    })
  }

  const handleCopyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      console.log('Copied to clipboard:', text)
    }).catch(err => {
      console.error('Failed to copy:', err)
    })
  }

  const handleClearSearch = () => {
    setFormData({
      query: "",
      status: true,
      route: {
        query: "",
        source: {
          type: "Source",
          data: {
            id: "",
            alias: "",
            name: ""
          }
        },
        target: {
          type: "Location",
          data: {
            criteria0: "",
            criteria1: "",
            criteria2: "",
            criteria3: "",
            criteria4: ""
          }
        }
      },
      courier: {
        query: "",
        name: "",
        alias: "",
        shippingType: "",
        deliveryType: "",
        channel: ""
      }
    })
    setSearchResults(null)
    setIsFormCollapsed(false)
    setCurrentPage(1)
    setSelectedCoverages(new Set())
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handleToggleCollapse = () => {
    setIsFormCollapsed(!isFormCollapsed)
  }

  const handleTransferClick = () => {
    setShowTransferForm(true)
  }

  const isObjectNotEmpty = (obj: Record<string, any>): boolean => {
    return Object.keys(obj).some(key => {
      const value = obj[key];
      return value !== '' && value !== null && value !== undefined && 
             (!Array.isArray(value) || value.length > 0) && 
             (typeof value !== 'object' || isObjectNotEmpty(value));
    });
  };

  const handleTransferSubmit = async (transferData: TransferFormData, selectedRoute:RouteData, selectedCourier: CourierData, routeSearchMode: 'search' | 'changeSource' | 'changeTarget') => {   
    const selectedCoveragesDetails = searchResults.records.filter((record: any) =>
      selectedCoverages.has(record.id)
    );

    const body = {
      upsert: {
        "route": routeSearchMode === 'changeSource' || routeSearchMode === 'changeTarget' ? {
          "source": isObjectNotEmpty(transferData.route.source.data) ? transferData.route.source : undefined,
          "target": isObjectNotEmpty(transferData.route.target.data) ? transferData.route.target : undefined,
        } : undefined,
        "routeId" : routeSearchMode === 'search' ? selectedRoute?.id || undefined : undefined,
        "courierId" : selectedCourier?.id || undefined,
        "leadtime" : transferData?.leadtime || undefined,
      },
      records: selectedCoveragesDetails
    }
    
    try {
      await post_transfer_combined_coverages_action({ body })
      Swal.fire({
        icon: "success",
        title: "Transferencia realizada con exito!",
        text: "Se han transferido las coberturas seleccionadas.",
      });
    } catch (error) {
      // Error is already handled by the hook with SweetAlert
      console.error('Search failed:', error)
    }
    
    // For now, just close the form and clear selections
    setShowTransferForm(false)
    setSelectedCoverages(new Set())
    
    // Optionally refresh the search results
    // handleFormSubmit(formData)
  }

  const handleTransferCancel = () => {
    setShowTransferForm(false)
  }

  const handleDuplicate = (cardId: string, coverage: any) => {
    // Create a copy without the ID
    const duplicatedCoverage = {
      ...coverage,
      id: undefined, // Remove ID to avoid updating existing coverage
    };
    
    // Generate a temporary ID for the duplicated coverage
    const tempId = `temp-${Date.now()}`;
    duplicatedCoverage.tempId = tempId;
    
    // Add to the beginning of search results
    setSearchResults((prevResults: any) => ({
      ...prevResults,
      records: [duplicatedCoverage, ...prevResults.records]
    }));
    
    // Set as expanded and in edit mode
    setExpandedCards((prev: any) => new Set([tempId, ...prev]));
    setDuplicatedCoverageId(tempId);
    
    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const onEditCard = async (cardId: string, updatedCoverage: any) => {
    // If this is a duplicated coverage being cancelled
    if (duplicatedCoverageId === cardId && !updatedCoverage) {
      // Remove from search results
      setSearchResults((prevResults: any) => ({
        ...prevResults,
        records: prevResults.records.filter((coverage: any) => 
          coverage.tempId !== cardId && coverage.id !== cardId
        )
      }));
      
      // Remove from expanded cards
      setExpandedCards(prev => {
        const newSet = new Set(prev);
        newSet.delete(cardId);
        return newSet;
      });
      
      setDuplicatedCoverageId(null);
      return;
    }
    
    // Handle normal save logic
    setSearchResults((prevResults: any) => ({
      ...prevResults,
      records: prevResults.records.map((coverage: { id: string }) => 
        coverage.id === cardId ? {...coverage, ...updatedCoverage} : coverage        )
    }));
    handleFormSubmit(formData);
    
    // Clear duplicated coverage ID if this was a successful save
    if (duplicatedCoverageId === cardId) {
      setDuplicatedCoverageId(null);
    }
  };

  return (
    <div className="w-full h-full flex flex-col items-center justify-start">
      <Breadcrumb data={[{ name: "Mantención De Coberturas", href: "/coverages/manage" }]} />
      <div className="w-full flex flex-col justify-center items-center py-4 px-6">
        <TitleScreen title="Mantención De Coberturas" />
      </div>
      <div className="w-full flex flex-col gap-4 px-6 animate-fade-in">
        {/* Search Form */}
        <CoverageSearchForm
          formData={formData}
          onFormDataChange={setFormData}
          onSubmit={handleFormSubmit}
          onClearSearch={handleClearSearch}
          isCollapsed={isFormCollapsed}
          onToggleCollapse={handleToggleCollapse}
          hasResults={!!searchResults}
          selectedCount={selectedCoverages.size}
          onTransferClick={handleTransferClick}
        />

        {/* Results Section */}
        {searchResults && (
          <CoverageResults
            searchResults={searchResults}
            selectedCoverages={selectedCoverages}
            expandedCards={expandedCards}
            currentPage={currentPage}
            resultsPerPage={resultsPerPage}
            onCoverageSelect={handleCoverageSelect}
            onSelectAll={handleSelectAll}
            onToggleExpansion={handleToggleExpansion}
            onCopyToClipboard={handleCopyToClipboard}
            onPageChange={handlePageChange}
            onClearSearch={handleClearSearch}
            onEdit={onEditCard}
            onDuplicate={handleDuplicate}
          />
        )}

        {/* Transfer Form Modal */}
        {showTransferForm && (
          <TransferForm
            selectedCount={selectedCoverages.size}
            onSubmit={handleTransferSubmit}
            onCancel={handleTransferCancel}
          />
        )}
      </div>
    </div>
  )
}

export default ListScreen
